import type { ComponentType, MouseEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import SearchImageCarouselWrapper from 'components/SearchKit/SearchTiles/SearchImageCarouselWrapper'
import SearchTileRowOverlineLocation from 'components/SearchKit/SearchTiles/shared/SearchTileRowOverlineLocation'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import { nonNullable } from 'lib/array/arrayUtils'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LEOfferPriceDetails from 'components/OfferList/OfferCards/LEOfferPriceDetails'
import BedbankOfferCardPriceDetails from 'components/OfferList/OfferCards/BedbankOfferCardPriceDetails'
import { mapOfferToHotelTileFeatures } from 'tripPlanner/mappers/hotelTileFeatures'
import { isBedbankOffer, isFlashHotelOffer } from 'lib/offer/offerTypes'
import { useAppSelector } from 'hooks/reduxHooks'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { Card, Hero, Content } from './ChatHotelOfferCardStyles'
import HotelCardFeatures from './ChatHotelCardFeatures'
import ChatHotelCarouselPrice from './ChatHotelCarouselPrice'
import getResizableImage from 'components/OfferList/OfferCards/getResizableImage'
import { COMPACT_HOTEL_CARD_WIDTH } from './CarouselOfferCardStyles'

const NO_URGENCY_LABELS: Array<App.OfferUrgencyLabel> = []
const HOTEL_IMAGE_PARAMS: ImageParams = {
  aspectRatio: '16:9',
  width: COMPACT_HOTEL_CARD_WIDTH,
  mobileWidth: `${COMPACT_HOTEL_CARD_WIDTH}px`,
  fit: 'center',
}

const HotelLabels = styled(LabelGroup)`
  position: relative;
  z-index: 2;
`

const TitleGroup = styled(Group)`
  min-width: 0;
`

const HotelImageCarousel = styled(SearchImageCarouselWrapper)`
  height: 100%;
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.BedbankOfferWithRate
  detailAmenities?: Array<App.OfferInclusion>
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  CtaComponent?: ComponentType
  BedbankPricingComponent?: ComponentType
  onImageClick: () => void
  onMoreClick: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
}

function ChatHotelOfferCard({ offer, detailAmenities, selectedDatesAndRoomsFilter, CtaComponent, BedbankPricingComponent, onMoreClick, onImageClick }: Props) {
  const intl = useIntl()
  const isBedbank = isBedbankOffer(offer)
  const tier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const hotelPackage = !isBedbankOffer(offer) ? offer.lowestPricePackage : undefined
  const luxPlusInclusions = hotelPackage?.luxPlusInclusionsByTier?.[tier]
  const memberPrice = hotelPackage?.memberPrice ?? 0
  const saveAmount = isLuxPlusEnabled && memberPrice > 0 ? (hotelPackage?.price ?? 0) - memberPrice : undefined
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])
  const images = useMemo(() => {
    if (offer.images.length) return nonNullable(offer.images.map(getResizableImage))
    const image = getResizableImage(offer.image)
    return image ? [image] : []
  }, [offer.image, offer.images])
  const charterBundleLabel = useMemo(() => {
    if (!isFlashHotelOffer(offer) || !offer.offerFlightsEnabled || !offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)) return undefined
    return <NeutralLabel>{intl.formatMessage(flightBundleGlossaryMessages.hotelAndFlightBundle)}</NeutralLabel>
  }, [offer, intl])
  const { inclusions, tileAmenities: amenities } = useMemo(
    () => mapOfferToHotelTileFeatures(offer, detailAmenities),
    [offer, detailAmenities],
  )
  const locations = useMemo(() => {
    if (isBedbankOffer(offer)) return nonNullable([offer.property.address.city, offer.property.address.countryName]).filter(Boolean)
    return nonNullable([offer.locationHeading, offer.locationSubheading]).filter(Boolean)
  }, [offer])
  const description = useMemo(() => {
    if (inclusions.length || amenities.length) return undefined
    if (!isBedbankOffer(offer)) return offer.name
    return offer.amenities || undefined
  }, [offer, inclusions.length, amenities.length])
  return <ProductPaletteProvider product={offer}>
    <Card background="normal-background" border="light-foreground" borderRadius="L" data-testid="chat-hotel-offer-card">
      <Group direction="vertical" fullHeight>
        <Hero>
          <HotelImageCarousel
            images={images}
            imageParams={HOTEL_IMAGE_PARAMS}
            onImageLoad={handleImageLoaded}
            onClick={onImageClick}
            arrowStyle="ghost"
          />
        </Hero>
        <Content padding="16 16 8">
          <Group direction="vertical" verticalAlign="space-between" gap={12} fullHeight>
            <Group direction="vertical" gap={12}>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={8}>
                <TitleGroup direction="vertical" gap={4} grow>
                  {locations.length > 0 && <SearchTileRowOverlineLocation locations={locations} />}
                  <Heading variant="heading5" lineClamp={2}>{offer.property.name}</Heading>
                </TitleGroup>
                {isOfferRatingDisplayable(offer.property.rating) && <OfferRating variant="xs" rating={offer.property.rating} onlyNumber inlineLabel hideLink />}
              </Group>
              <HotelCardFeatures inclusions={inclusions} amenities={amenities} description={description} onMoreClick={onMoreClick} />
              <HotelLabels>
                {charterBundleLabel}
                {!isBedbank && <OfferLabels offer={offer} urgencyLabels={NO_URGENCY_LABELS} luxPlusInclusions={luxPlusInclusions} showMemberInclusionLabels={!!luxPlusInclusions?.length} saveAmount={saveAmount} leadWithLuxPlusPrice />}
              </HotelLabels>
            </Group>
            {isBedbank && <BedbankOfferCardPriceDetails
              offer={offer}
              selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
              PricingComponent={BedbankPricingComponent}
              CtaComponent={CtaComponent}
              showViewButton={false}
            />}
            {!isBedbank && <LEOfferPriceDetails
              offer={offer}
              selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
              PricingComponent={ChatHotelCarouselPrice}
              CtaComponent={CtaComponent}
              showViewButton={false}
            />}
          </Group>
        </Content>
      </Group>
    </Card>
  </ProductPaletteProvider>
}

export default ChatHotelOfferCard
